/**
 * Profile Constants
 * Shared constants for profile management
 */

/**
 * Validation rules for profile fields
 */
export const VALIDATION_RULES = {
  password: {
    minLength: 8,
    maxLength: 128,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
    requireSpecialChar: false, // Removed for MVP
  },
  username: {
    minLength: 3,
    maxLength: 30,
    pattern: /^[a-zA-Z0-9_-]+$/,
  },
  name: {
    minLength: 2,
    maxLength: 50,
  },
  firstName: {
    minLength: 2,
    maxLength: 50,
  },
  lastName: {
    minLength: 2,
    maxLength: 50,
  },
  email: {
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    maxLength: 255,
  },
  company: {
    maxLength: 100,
  },
  bio: {
    maxLength: 500,
  },
  phone: {
    pattern: /^[+]?[1-9][\d]{0,15}$/,
  },
}

/**
 * Timezone options.
 *
 * `label` is a locale-NEUTRAL English fallback only: UI components MUST render
 * these labels through i18n (`t('preferences.options.timezones.<value>',
 * { defaultValue: option.label })` — keys live in the backend `profile`
 * namespace locales). Never add translated text here.
 */
export const TIMEZONE_OPTIONS = [
  // The NAME only: the picker appends the offset in force today (`utcOffsetLabel`),
  // because a fixed «(UTC+1)» is false for Madrid half the year.
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/Madrid', label: 'Madrid' },
  { value: 'Europe/London', label: 'London' },
  { value: 'America/New_York', label: 'New York' },
  { value: 'America/Los_Angeles', label: 'Los Angeles' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
  { value: 'Australia/Sydney', label: 'Sydney' },
]

/**
 * Currency options.
 *
 * `label` is a locale-NEUTRAL English fallback only: UI components MUST render
 * these labels through i18n (`t('preferences.options.currencies.<value>',
 * { defaultValue: option.label })`). Never add translated text here.
 */
export const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'EUR - Euro (€)' },
  { value: 'USD', label: 'USD - Dollar ($)' },
  { value: 'GBP', label: 'GBP - Pound (£)' },
  { value: 'JPY', label: 'JPY - Yen (¥)' },
  { value: 'CNY', label: 'CNY - Yuan (¥)' },
  { value: 'CHF', label: 'CHF - Swiss Franc' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
]

/**
 * Language options
 */
export const LANGUAGE_OPTIONS = [
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'it', label: 'Italiano', flag: '🇮🇹' },
  { value: 'pt', label: 'Português', flag: '🇵🇹' },
]

/**
 * Date format options — every token the client's `dateFormat` config can hold.
 *
 * `as const` is LOAD-BEARING: `date-format.ts` derives the `DateFormatValue` literal union
 * from these `value` tokens, and both the frontend `EuropeanDateFormat` type and the backend
 * date-filter enum derive from that union. Without it every consumer degrades to `string`.
 *
 * NOTE the deliberate difference from SUPPORTED_DATE_FORMATS below: this list is a SUPERSET.
 * 'DD.MM.YYYY' is the SHIPPED DEFAULT of the frontend `userConfigService`, so it is what a
 * user who never opened Preferences sends — but it is not (yet) a persistable preference
 * value. Any request enum must derive from THIS list, never from the shorter one.
 */
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2024)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
] as const

/**
 * SUPPORTED_DATE_FORMATS — single source of truth for the date format
 * tokens accepted by `UserPreferences.dateFormat` across backend + frontend.
 *
 * - The backend zod validator (`backendUserPreferencesSchema.dateFormat`) MUST
 *   only accept values from this list (currently the slash + ISO subset; the
 *   dotted EU variant `DD.MM.YYYY` is rendered client-side via
 *   `user-config.service.ts` but is not yet a valid stored preference value).
 * - The frontend `formatters.ts` `dateFormats` map MUST contain a renderer for
 *   every token here.
 * - The user-preferences default (`DEFAULT_USER_PREFERENCES.dateFormat`) MUST
 *   be one of these values.
 *
 * IMPORTANT: components rendering dates MUST read `user.preferences.dateFormat`
 * at runtime — they MUST NOT hardcode a token from this list as a global
 * default, otherwise non-default locales render the wrong format.
 */
export const SUPPORTED_DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'] as const

export type SupportedDateFormat = (typeof SUPPORTED_DATE_FORMATS)[number]

/**
 * DEFAULT_DATE_FORMAT — used by the backend when seeding new users.
 * EU format because the product's primary market is Spanish/EU (default
 * language is 'es'). DO NOT change without updating
 * `DEFAULT_USER_PREFERENCES.dateFormat` in
 * `packages/backend/src/platform/auth/constants/auth.constants.ts` AND the
 * fallback in `packages/backend/src/platform/auth/domain/entities/User.ts`.
 */
export const DEFAULT_DATE_FORMAT: SupportedDateFormat = 'DD/MM/YYYY'

/**
 * Number format options.
 *
 * `value` tokens are the persisted preference identifiers (historic Spanish
 * names — do NOT rename, they live in users' stored preferences). `label` is a
 * locale-NEUTRAL English fallback: UI components MUST render through i18n
 * (`t('preferences.options.numberFormat.<value>', { defaultValue: option.label })`).
 *
 * `as const` is LOAD-BEARING: `number-format.ts` derives the
 * `ConfigurableNumberFormat` literal union from these `value` tokens, and both
 * the frontend `NumberFormat` type and the backend amount-filter enum derive
 * from that union. Without it every consumer degrades to `string` and the two
 * sides can drift into a 400 again.
 */
export const NUMBER_FORMAT_OPTIONS = [
  { value: 'europeo-puntos', label: 'European with dots (1.234.567,89)' },
  { value: 'europeo-sin-puntos', label: 'European without dots (1234567,89)' },
  { value: 'americano-comas', label: 'American with commas (1,234,567.89)' },
  { value: 'americano-sin-comas', label: 'American without commas (1234567.89)' },
] as const

/**
 * Theme options.
 *
 * Only 'dark' | 'light' | 'system' — matching the frontend theme engine
 * (`ThemeMode` in packages/frontend/src/shared/theme/ThemeProvider.tsx).
 * The legacy 'auto' entry was REMOVED (it duplicated 'system' and the theme
 * engine never understood it); persisted legacy 'auto' values are normalized
 * to 'system' at every read site. `label` is a locale-NEUTRAL English
 * fallback: UI components MUST render through i18n
 * (`t('preferences.options.theme.<value>', { defaultValue: option.label })`).
 */
export const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
]

/**
 * Profile visibility options.
 *
 * NOTE: currently UNUSED — no frontend/backend consumer imports this constant
 * (verified 2026-07). Kept for the future privacy settings UI; labels are
 * still Spanish-only and must be routed through i18n before first real use.
 */
export const PROFILE_VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Público' },
  { value: 'private', label: 'Privado' },
  { value: 'contacts', label: 'Solo contactos' },
]

/**
 * Session timeout options (in minutes).
 *
 * NOTE: currently UNUSED — no frontend/backend consumer imports this constant
 * (verified 2026-07; the real session-TTL whitelist lives in the backend
 * preferences validator). Labels are still Spanish-only and must be routed
 * through i18n before first real use.
 */
export const SESSION_TIMEOUT_OPTIONS = [
  { value: 15, label: '15 minutos' },
  { value: 30, label: '30 minutos' },
  { value: 60, label: '1 hora' },
  { value: 120, label: '2 horas' },
  { value: 480, label: '8 horas' },
  { value: 1440, label: '24 horas' },
]
